/* Copyright (c) 2016-2023 Planticle Pty Ltd */
"use strict";

import birdata from "../api.js";
import ko from "knockout";
import _ from "underscore";
import util from "../util/util.js";
import fieldManager from "../util/fields.js";

var program = {
	code: "bwb",
	name: "Beach-washed Birds",
	description: `
<p>The Birdlife Australia Seabird Group are supporting the Beach-washed Birds programme. This
programme targets data on dead seabirds washed ashore. We would much prefer to watch
seabirds alive at sea or at their breeding colonies. However, not infrequently dead seabirds are
washed ashore, sometimes in very high numbers. These mortality events can have a significant
effect on population numbers and the success of a breeding season, so it is important to record
and develop an understanding of these events.</p>

<p>Contributing to Beach-washed Birds is a valuable scientific endeavour and a rewarding pastime.
You can expect to</p>

<ul>
	<li>add to our knowledge of the distribution and life of our seabirds</li>
	<li>get a really close up view of their plumages to potentially identify age, sex and moult plumages</li>
	<li>improve your identification skills</li>
	<li>perhaps add a rarity, a new vagrant to your and Australia’s list, and</li>
	<li>improve your health with an energetic walk!</li>
</ul>

<p>This project is designed to collect information on dead seabirds. If you find injured or sick
seabirds, consider reporting to local wildlife carers. You can also report such observations in the
Birdata.</p>

<p>Dead birds other than seabirds can be recorded with this app. For example, the death of bush
birds from wildfire washed up on beaches can be recorded with this program.</p>

<p>Oil or other pollution disasters should be reported to the national scheme for these phenomena.
See <a href="https://www.amsa.gov.au/marine-environment/pollution-response" target="_blank">https://www.amsa.gov.au/marine-environment/pollution-response</a>.</p>

<h4>Data collection guidelines</h4>

<p>Beach-washed birds usually occur as either</p>

<ul>
	<li>a mass mortality with tens to many thousands of dead birds washed on up beaches usually as
a result of exhaustion after a migration or severe storm. These events typically include
individuals of the same species.</li>

Or,

	<li>isolated individual/s dying and washed ashore.</li>
</ul>

<p>The first kind of observation is best treated as a count along a given stretch of beach. You can
define your own stretch of beach as a one-off- an “Ad-hoc route”,
make repeated searches for same stretch of coastline- an “Existing route”, or
use one of the predefined locations from any of the Birdata’s projects. Beach-nesting Birds
project has many such locations defined.</p>

<p>Your count can be for the entire stretch of the beach, especially where the dead birds are
scattered along the length of the route. Most counts for each species are likely to be for the
entire route.</p>

<p>However, if the specimens are strongly clustered, it may make more sense to count the
individuals for each species and cluster, and enter the data for these clusters with specific
locations within the defined stretch of beach.</p>

<p>Observations of the second kind of mortality where there are small numbers or isolated
specimens may be entered as point counts.</p>

<p>Where the species cannot be identified, Birdata has some generic categories.</p>

<h4>OH&S guidelines</h4>
<p>Avoid touching the dead birds unless wearing sterile gloves.</p>
	`,
	load: function() {
		return birdata.lookup().then(function(lookups) {
			program.options = lookups;
			return true;
		});
	},
	programData: function(survey) {
		var fields = program.surveyFields();

		var speciesList = ko.observable([]);
		birdata.speciesList().then(function(spp) { // Note this causes warning about promise not being returned
			// var list = spp.filter(function(sp) { return speciesIDs.includes(sp.id); });
			var list = spp.slice(0);
			// Currently this is hardcoded. Perhaps we should be requesting this some other way.
			list.push({
				"is_range_defined":false,
				"supertaxa":[],
				"taxon_id":"unk",
				"scientific_name":"Unknown spp",
				"id":100000000,
				"sensitivity":"not_sensitive",
				"sort":100000000,
				"common_name":"Unknown spp",
				"subspecies":[]});
			speciesList(list);
		});

		return {
			fields: fields,
			validations: _.values(fields),
			speciesList: speciesList
		};
	},
	fields: function() {
		return program.surveyFields();
	},
	surveyFields: function() {
		var fields = [
			{
				name: "additionalObservers",
				type: "text",
				label: "Additional observers"
			},
			{
				name: "surveyMeans",
				type: "options",
				label: "Survey conducted by",
				options: program.options.s2020_survey_means,
				validate: "required"
			},
			{
				name: "tide",
				type: "options",
				label: "Tide",
				options: program.options.s2020_tide
			}
		].map(fieldManager.field);

		fields = util.arrayToMap(fields, function(f) { return f.field.name; });

		return fields;
	},
	sightingFields: function(sightingModel) {
		var species = sightingModel ? sightingModel.species : null;

		// To avoid null issues
		function nestingCodeIsOneOf(/*args*/) {
			var nesting = fields.nesting();
			return _.contains(arguments, nesting && nesting.code);
		};

		var fields = [
			{
				name: "adultCount",
				type: "integer",
				label: "Adults",
				max: 99999,
				validate: "required",
				defaultValue: 0
			},
			{
				name: "juvenileCount",
				type: "integer",
				label: "Immature",
				max: 99999,
				validate: "required",
				defaultValue: 0
			},
			{
				name: "unknownLifeStageCount",
				type: "integer",
				label: "Unknown",
				max: 99999,
				validate: "required",
				defaultValue: 0
			},
			{
				name: "specimenFreshness",
				type: "options",
				multiselect: true,
				style: "checkbox",
				options: program.options.bwb_specimen_freshness,
				required: true,
				validate: function(f) {
					if(f.value().length == 0) {
						return "Freshness is required"
					}
				}
			},
			{
				name: "habitat",
				type: "options",
				label: "Habitat",
				options: program.options.bnb_habitat,
				validate: "required",
				defaultValue: _.findWhere(program.options.bnb_habitat, { code: "beach" })
			},
			{
				name: "pollutionPresent",
				type: "boolean",
				label: "Relevant pollution present",
				validate: "required"
			},
			{
				name: "pollutionComments",
				type: "text",
				placeholder: "If pollution or entanglement is involved with the dead seabirds, please provide a brief comment.",
				multiline: true,
				hidden: () => !fields.pollutionPresent()
			},
			{
				name: "tags",
				type: "multi-text",
				label: "Bands or flags",
				help: "e.g. PE Right (White); Red/Metal Left, Yellow/Orange Right; orange Right unknown ID",
				addLabel: "Add bands/flags for an individual"
			}
		].map(fieldManager.field);

		fields = util.arrayToMap(fields, function(f) { return f.field.name; });

		return fields;
	},
	updateSurveyJSON: function(json, survey) {
		var fields = survey.programData().bwb.fields;
		json.bwb = {};
		var route = survey.routeInfo();
		if(route) {
			if(route.sharedSite) {
				json.bwb.shared_site_id = route.sharedSite.id;
				json.bwb.partial_route = route.partialRoute;
			}
			if(route.start) {
				json.bwb.start_lon = route.start.lon;
				json.bwb.start_lat = route.start.lat;
			}
			if(route.finish) {
				json.bwb.finish_lon = route.finish.lon;
				json.bwb.finish_lat = route.finish.lat;
			}
		}
		json.all_species_recorded = false;
		_.each(json.sightings, function(sighting) {
			sighting.count = (+sighting.bwb.adult_count || 0) + (+sighting.bwb.juvenile_count || 0) + (+sighting.bwb.unknown_life_stage_count || 0);
		});

		_.each(fields, function(f) {
			f.field.save(json.bwb);
		});
	},
	hideWaterLevel: true,
	hideSurveyType: true,
	surveyTypeOptions: [],
	afterSelect: function(survey) {
		// Set survey type after program selected
		survey.type({
			code: 'fixed_route_search',
			name: 'Fixed route search'
		});
		survey.allSpeciesRecorded(false);
	},
	beforeDeselect: function(survey) {
		survey.type(null);
	},
	loadFromJSON: function(json, fields, survey) {
		var data = json.bwb;

		if(!data) return; //incomplete survey

		_.each(fields, function(f) {
			f.field.load(data);
		});
	},
	visibleSharedSiteLayers: () => ["bnb"],
	defaultAreaLayer: (layers) => null,
	updateMap: function(map, survey, areasPromise, locationViewModel) {
	},
	routeBasedEntry: true,
	existingRoutesDefinedBySharedSites: true,
	multipleSightingsPerSpecies: true,
	sightingLocations: true,
	forceSightingLocations: true,
	sightingNotesPlaceholder: 'Add additional observations. Suggest possible cause of wreck.',
	allSpeciesRecordedEnabled: function() { return false; },
	multipleDaysEnabled: function() { return false; },
	observerCountEnabled: function() { return true; }
};

export default program;
